import React, { useState, forwardRef } from "react";
import { useSelector } from "react-redux";
import { FormattedMessage } from "react-intl";
import Loader from "@amzn/meridian/loader";
import Box from "@amzn/meridian/box";
import Text from "@amzn/meridian/text";
import Row from "@amzn/meridian/row";
import WidgetHeader from "../components/WidgetHeader";
import EditWidgetConfigPanel from "../components/EditWidgetConfigPanel";
import { useWidgetLocalization } from "../util/WidgetLocalizationUtil";
import { ERROR_LOADING_WIDGET_DATA } from "../constants/Constants";

const BaseWidget = forwardRef(function BaseWidget(
  {
    widgetSpec,
    renderer,
    isPriorityDashboard = false,
    stationType,
    pageSelected,
    viewTypeSelected,
  },
  ref,
) {
  // Edit mode state
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Get localized widget fields
  const { title, description } = useWidgetLocalization(
    widgetSpec.widgetDefinition,
  );

  const metricData = useSelector((state) =>
    widgetSpec.widgetDefinition.metrics.map(
      (metric) => state.metricData[metric.id] || { loading: true },
    ),
  );

  const selectedRoles = useSelector((state) => state.selectedRoles || []);

  // Widget should only show alert styling if it's in alert AND the selected roles match the alert roles
  const IsWidgetInAlert = metricData.some((metric) => {
    if (!metric.alert || !metric.alert.isInAlert) {
      return false;
    }

    // If no roles are defined for the alert, show alert styling (backwards compatibility)
    if (!metric.alert.roles || !Array.isArray(metric.alert.roles)) {
      return true;
    }

    // Check if any of the selected roles match the alert roles
    const hasMatchingRole = metric.alert.roles.some((role) =>
      selectedRoles.includes(role),
    );
    return hasMatchingRole;
  });

  const renderInternalWidgetContent = () => {
    const loading = metricData.some((m) => m.loading);
    const error = metricData.find((m) => m.error)
      ? metricData.find((m) => m.error).error
      : null;

    // Simple loading/error states like mainline
    if (loading) {
      return (
        <Row
          height="100%"
          width="100%"
          alignmentHorizontal="center"
          alignmentVertical="center"
        >
          <Loader label="Loading widget" size="medium" />
        </Row>
      );
    }

    if (error) {
      return (
        <Row
          height="100%"
          width="100%"
          alignmentHorizontal="center"
          alignmentVertical="center"
        >
          <Text color="error">
            <FormattedMessage
              id="mySCC.baseWidget.errorLoadingWidgetData"
              defaultMessage={ERROR_LOADING_WIDGET_DATA}
            />
          </Text>
        </Row>
      );
    }

    return renderer.render(widgetSpec, { metricData });
  };

  // BaseWidget now provides the complete widget structure
  return (
    <Box
      ref={ref}
      type="outline"
      spacingInset="200 400"
      height="100%"
      className={`flex-column ${IsWidgetInAlert ? "widget-alert-border" : ""}`}
      {...(IsWidgetInAlert && { backgroundColor: "#F9D5E2" })}
    >
      <WidgetHeader
        title={title}
        description={description}
        widgetId={widgetSpec.widgetId}
        isEditMode={isPriorityDashboard ? false : true}
        onEditClick={() => setIsModalOpen(true)}
        isInAlert={IsWidgetInAlert}
        stationType={stationType}
        pageSelected={pageSelected}
        viewTypeSelected={viewTypeSelected}
      />
      {/* Renderer provides only the content */}
      {renderInternalWidgetContent()}
      <EditWidgetConfigPanel
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        widgetId={widgetSpec.widgetId}
        widgetConfig={widgetSpec}
      />
    </Box>
  );
});

export default BaseWidget;



// WEBPACK FOOTER //
// ./src/components/mySCC/common/BaseWidget.js